import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
    CreatePatientRequest,
    PatientResponse,
    PatientSummary
} from '../models/patient.model';
import { createNoCacheOptions } from '../utils/http-headers.util';

export interface Transcription {
  id: string;
  date: string;
  specialty: string;
  chiefComplaint?: string;
  duration: string;
  status: string;
  patientId?: string;
  patientName?: string;
  mrn?: string;
  content?: string;
  soapNotesAvailable?: boolean;
  soapUnread?: boolean;
  visitReason?: string;
  appointmentTime?: string;
  appointmentType?: string;
  createdByUserName?: string;
  analysis?: TranscriptionDetailResponse['analysis'];
}

export interface TranscriptionDetailResponse {
  id: string;
  originalFileName: string;
  transcriptionUrl: string;
  status: string;
  specialty: string;
  analysisId?: string;
  error?: string;
  patientId: number;
  date?: string; // Add date property for template compatibility
  duration?: string; // Add duration property for template compatibility
  
  // Appointment fields
  appointmentType?: string;
  chiefComplaint?: string;
  appointmentTime?: string;
  createdByUserName?: string;
  createdByUserId?: string;
  
  // Patient information
  patientName: string;
  mrn: string;
  dateOfBirth?: string;
  patientActive: boolean;
  
  // Analysis results
  analysis?: {
    id: string;
    transcriptUrl?: string;
    summary?: string;
    diagnosis?: string;
    prescription?: {
      medicationName?: string;
      dosage?: string;
      frequency?: string;
      duration?: string;
      instructions?: string;
    };
    soapNote?: {
      subjective?: string;
      objective?: string;
      assessment?: string;
      plan?: string;
      patientInstructions?: string;
      assessmentSummary?: string;
      problems?: { name?: string; assessment?: string; plan?: string }[];
    };
    aiAnalysis?: {
      keyFindings?: string[];
      treatmentPlanSummary?: string[];
    };
    specialty?: string;
    analysisData?: string;
    status?: string;
  };
  soapNotesAvailable?: boolean;
  soapUnread?: boolean;
  visitReason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PatientsService {
  private baseUrl = `${environment.apiUrl}/patients`; // Backend API URL

  constructor(private http: HttpClient) { }

  // New API methods for backend integration
  createPatient(patientRequest: CreatePatientRequest): Observable<PatientResponse> {
    return this.http.post<PatientResponse>(this.baseUrl, patientRequest)
      .pipe(
        catchError(error => {
          console.error('Error creating patient:', error);
          throw error;
        })
      );
  }

  getAllPatients(): Observable<PatientResponse[]> {
    return this.http.get<PatientResponse[]>(this.baseUrl, createNoCacheOptions())
      .pipe(
        catchError(error => {
          console.error('Error fetching patients:', error);
          throw error;
        })
      );
  }

  getPatientById(id: number): Observable<PatientResponse> {
    return this.http.get<PatientResponse>(`${this.baseUrl}/${id}`, createNoCacheOptions())
      .pipe(
        catchError(error => {
          console.error('Error fetching patient:', error);
          throw error;
        })
      );
  }

  updatePatient(id: number, patientRequest: CreatePatientRequest): Observable<PatientResponse> {
    return this.http.put<PatientResponse>(`${this.baseUrl}/${id}`, patientRequest)
      .pipe(
        catchError(error => {
          console.error('Error updating patient:', error);
          throw error;
        })
      );
  }

  deletePatient(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        catchError(error => {
          console.error('Error deleting patient:', error);
          throw error;
        })
      );
  }

  /**
   * Get patients filtered by search term
   */
  searchPatients(searchTerm: string): Observable<PatientResponse[]> {
    return this.getAllPatients().pipe(
      map(patients => {
        if (!searchTerm.trim()) {
          return patients;
        }
        
        return patients.filter(patient =>
          `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          patient.mrn.includes(searchTerm)
        );
      })
    );
  }

  /**
   * Get a patient by MRN
   */
  getPatientByMRN(mrn: string): Observable<PatientResponse | undefined> {
    return this.getAllPatients().pipe(
      map(patients => patients.find(p => p.mrn === mrn))
    );
  }

  /**
   * Get all patient summaries (ID, name, MRN only) for fast autocomplete
   * @returns list of patient summaries
   */
  getPatientSummaries(): Observable<PatientSummary[]> {
    return this.http.get<PatientSummary[]>(`${this.baseUrl}/summaries`, createNoCacheOptions())
      .pipe(
        catchError(error => {
          throw error;
        })
      );
  }

  /**
   * Get patient statistics (aggregated data for dashboard)
   * @returns Observable of patient statistics
   */
  getPatientStatistics(): Observable<{
    totalPatients: number;
    newThisMonth: number;
    appointmentsThisWeek: number;
    averageVisitsPerPatient: number;
  }> {
    return this.http.get<{
      totalPatients: number;
      newThisMonth: number;
      appointmentsThisWeek: number;
      averageVisitsPerPatient: number;
    }>(`${this.baseUrl}/statistics`, createNoCacheOptions())
      .pipe(
        catchError(error => {
          console.error('Error fetching patient statistics:', error);
          throw error;
        })
      );
  }

  /**
   * Get all transcriptions for a specific patient by MRN
   * @param mrn Patient's Medical Record Number
   * @returns Observable of transcription list
   */
  getTranscriptionsByPatientMrn(mrn: string): Observable<Transcription[]> {
    const url = `${this.baseUrl}/${mrn}/transcriptions`;
    
    return this.http.get<TranscriptionDetailResponse[]>(url, 
      createNoCacheOptions({ 'api-key': environment.apiKey })
    ).pipe(
      map((transcriptions: TranscriptionDetailResponse[]) => {
        return transcriptions.map(transcription => this.convertToTranscription(transcription));
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('PatientsService: Error fetching patient transcriptions:', error);
        return of([]);
      })
    );
  }

  private convertToTranscription(transcriptionDetail: TranscriptionDetailResponse): Transcription {
    return {
      id: transcriptionDetail.id,
      date: transcriptionDetail.date || new Date().toISOString().split('T')[0],
      specialty: transcriptionDetail.specialty || 'General',
      duration: transcriptionDetail.duration || 'Unknown',
      status: transcriptionDetail.status,
      patientId: transcriptionDetail.patientId.toString(),
      patientName: transcriptionDetail.patientName,
      mrn: transcriptionDetail.mrn,
      content: transcriptionDetail.transcriptionUrl,
      soapNotesAvailable: transcriptionDetail.soapNotesAvailable === true,
      soapUnread: transcriptionDetail.soapUnread === true,
      visitReason: transcriptionDetail.visitReason,
      appointmentTime: transcriptionDetail.appointmentTime,
      appointmentType: transcriptionDetail.appointmentType,
      createdByUserName: transcriptionDetail.createdByUserName,
      analysis: transcriptionDetail.analysis,
    };
  }
}
